import { useNavigate, useSearch } from '@tanstack/react-router';
import { EyeOff, Pin, PinOff, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataTable,
  useDataTable,
  useSearchListState,
  type BulkAction,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import type {
  BulkResponse,
  ProductSchedule,
  ProductSelection,
  ScheduleMember,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { BulkResultDialog } from '@/shared/bulk/bulk-result-dialog';
import { useSelectionRequest } from '@/shared/bulk/use-selection-request';

import type { ScheduleAction } from '../api/schedule-errors';
import type { MemberBulkAction } from '../api/schedules-api';
import { useBulkScheduleMembers } from '../hooks/use-bulk-schedule-members';
import { useAddScheduleMembers, useUpdateScheduleMember } from '../hooks/use-member-mutations';
import { useScheduleMembers } from '../hooks/use-schedule-members';
import { excludedInstead, memberAddOutcome } from '../lib/member-add';
import { nextUpIds } from '../lib/next-up';
import { memberListParams } from '../schemas/schedule-page-search';

import { AddMembersDialog } from './add-members-dialog';
import { ConfirmOfflineDialog, type ConfirmOfflineRequest } from './confirm-offline-dialog';
import { useMemberColumns } from './member-columns';
import { MemberResultDetail } from './member-result-detail';
import { RemoveMemberDialog } from './remove-member-dialog';
import { ScheduleErrorAlert } from './schedule-error-alert';
import { memberFilters } from './schedule-filters';

const ROUTE_ID = '/_authed/_active/org/schedules/$scheduleId' as const;
const ROUTE_PATH = '/org/schedules/$scheduleId' as const;

const liveStoreCount = (member: ScheduleMember) =>
  member.placements.filter((p) => p.state === 'live').length;

interface PoolResult {
  response: BulkResponse;
  description: string;
}

export interface PoolTabProps {
  schedule: ProductSchedule;
  canWrite: boolean;
  /** Opens a run on the Runs tab: the release run an exclude or a remove ran. */
  onOpenRun: (runId: string) => void;
}

/**
 * The pool: hand-picked and rule members with their state per store. Pin keeps one live on every
 * run, exclude keeps the schedule's hands off it, position breaks ties in the batch order. Rows
 * can be selected for pin, unpin, exclude, include and remove in bulk; anything that takes a
 * live product offline asks first and offers the run it ran.
 */
export function PoolTab({ schedule, canWrite, onOpenRun }: PoolTabProps) {
  const { t } = useTranslation('schedules');
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const filters = useMemo(() => memberFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const params = useMemo(() => memberListParams(list.params), [list.params]);
  const query = useScheduleMembers(schedule.id, params);
  const add = useAddScheduleMembers(schedule.id);
  const selectionRequest = useSelectionRequest();
  const update = useUpdateScheduleMember(schedule.id);
  const bulk = useBulkScheduleMembers(schedule.id);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [failure, setFailure] = useState<{ error: unknown; action: ScheduleAction } | null>(null);
  const [titles, setTitles] = useState<ReadonlyMap<string, string>>(new Map());
  const [result, setResult] = useState<PoolResult | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ScheduleMember | null>(null);
  const [confirm, setConfirm] = useState<ConfirmOfflineRequest | null>(null);
  const storeNames = useMemo(
    () => new Map(schedule.stores.map((s) => [s.storeId, s.storeName])),
    [schedule.stores],
  );
  const items = useMemo(() => query.data?.items ?? [], [query.data?.items]);

  /** A change that ran a release run: say so and offer to open it. */
  const offerRun = useCallback(
    (runId: string) =>
      toast.success(t('toasts.takenOffline'), {
        action: { label: t('toasts.openRun'), onClick: () => onOpenRun(runId) },
      }),
    [onOpenRun, t],
  );

  const send = useCallback(
    (member: ScheduleMember, body: { pinned?: boolean; excluded?: boolean; position?: number }) => {
      setFailure(null);
      setBusy((prev) => new Set(prev).add(member.productId));
      update.mutate(
        { productId: member.productId, body },
        {
          onSuccess: (change) => {
            if (change.releaseRunId) offerRun(change.releaseRunId);
          },
          onError: (error) => {
            setTitles(new Map([[member.productId, member.product.title]]));
            setFailure({ error, action: 'updateMember' });
          },
          onSettled: () =>
            setBusy((prev) => {
              const next = new Set(prev);
              next.delete(member.productId);
              return next;
            }),
        },
      );
    },
    [offerRun, update],
  );

  // Excluding a member that is live somewhere unpublishes it there now, so it asks first.
  const onChange = useCallback(
    (member: ScheduleMember, body: { pinned?: boolean; excluded?: boolean; position?: number }) => {
      const live = liveStoreCount(member);
      if (body.excluded === true && live > 0) {
        setConfirm({
          title: t('pool.exclude.title', { title: member.product.title }),
          description: t('pool.exclude.live', { count: live }),
          confirmLabel: t('pool.exclude.confirm'),
          onConfirm: () => send(member, body),
        });
        return;
      }
      send(member, body);
    },
    [send, t],
  );

  // A hint only: worked out from the loaded rows, so shown when the whole pool is on this page.
  const wholePool =
    !query.data?.nextCursor &&
    !list.q &&
    Object.keys(list.filters).length === 0 &&
    items.length === schedule.members.total;
  const nextUp = useMemo(
    () =>
      wholePool && schedule.status !== 'ended'
        ? nextUpIds(items, schedule.liveCount)
        : new Set<string>(),
    [items, schedule.liveCount, schedule.status, wholePool],
  );

  const columns = useMemberColumns({
    stores: schedule.stores,
    canWrite,
    busy,
    nextUp,
    onChange,
  });
  const rowActions = useMemo<Array<RowAction<ScheduleMember>>>(
    () =>
      canWrite
        ? [
            {
              id: 'remove',
              label: t('pool.remove.action'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              onAction: (row) => setRemoving(row),
            },
          ]
        : [],
    [canWrite, t],
  );
  const table = useDataTable({
    mode: 'server',
    data: items,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.productId,
    tableId: 'schedule-pool',
  });
  const clearSelection = table.selection.clear;

  const runBulk = useCallback(
    (action: MemberBulkAction, rows: readonly ScheduleMember[]) => {
      setFailure(null);
      setTitles(new Map(rows.map((row) => [row.productId, row.product.title])));
      bulk.mutate(
        { productIds: rows.map((row) => row.productId), action },
        {
          onSuccess: (response) => {
            clearSelection();
            const instead = action === 'remove' ? excludedInstead(response) : 0;
            if (response.failed > 0) {
              setResult({ response, description: t('pool.changeResultDescription') });
            } else if (response.succeeded > instead) {
              toast.success(t(`toasts.bulk.${action}`, { count: response.succeeded - instead }));
            }
            if (instead > 0) toast.info(t('toasts.excludedInstead', { count: instead }));
            if (response.releaseRunId) offerRun(response.releaseRunId);
          },
          onError: (error) =>
            setFailure({ error, action: action === 'remove' ? 'removeMember' : 'updateMember' }),
        },
      );
    },
    [bulk, clearSelection, offerRun, t],
  );

  /** Exclude and remove take live members offline now: say how many before going ahead. */
  const confirmBulk = useCallback(
    (action: 'exclude' | 'remove', rows: readonly ScheduleMember[]) => {
      const live = rows.filter((row) => liveStoreCount(row) > 0).length;
      const description = [
        t(action === 'exclude' ? 'pool.bulk.excludeDescription' : 'pool.bulk.removeDescription'),
        live > 0 ? t('pool.bulk.live', { count: live }) : null,
      ]
        .filter(Boolean)
        .join(' ');
      setConfirm({
        title: t(action === 'exclude' ? 'pool.bulk.excludeTitle' : 'pool.bulk.removeTitle', {
          count: rows.length,
        }),
        description,
        confirmLabel: t(
          action === 'exclude' ? 'pool.bulk.excludeConfirm' : 'pool.bulk.removeConfirm',
        ),
        onConfirm: () => runBulk(action, rows),
      });
    },
    [runBulk, t],
  );

  const bulkActions = useMemo<Array<BulkAction<ScheduleMember>>>(
    () =>
      canWrite
        ? [
            {
              id: 'pin',
              label: t('pool.bulk.pin'),
              icon: <Pin aria-hidden />,
              promoted: true,
              keepSelection: true,
              disabled: bulk.isPending,
              onAction: (_selection, rows) => runBulk('pin', rows),
            },
            {
              id: 'unpin',
              label: t('pool.bulk.unpin'),
              icon: <PinOff aria-hidden />,
              keepSelection: true,
              disabled: bulk.isPending,
              onAction: (_selection, rows) => runBulk('unpin', rows),
            },
            {
              id: 'exclude',
              label: t('pool.bulk.exclude'),
              icon: <EyeOff aria-hidden />,
              promoted: true,
              keepSelection: true,
              disabled: bulk.isPending,
              onAction: (_selection, rows) => confirmBulk('exclude', rows),
            },
            {
              id: 'include',
              label: t('pool.bulk.include'),
              icon: <RotateCcw aria-hidden />,
              keepSelection: true,
              disabled: bulk.isPending,
              onAction: (_selection, rows) => runBulk('include', rows),
            },
            {
              id: 'remove',
              label: t('pool.bulk.remove'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              keepSelection: true,
              disabled: bulk.isPending,
              onAction: (_selection, rows) => confirmBulk('remove', rows),
            },
          ]
        : [],
    [bulk.isPending, canWrite, confirmBulk, runBulk, t],
  );

  const addProducts = async ({
    selection,
    titles: picked,
    pinned,
  }: {
    selection: ProductSelection;
    titles: Map<string, string>;
    pinned: boolean;
  }) => {
    setAdding(false);
    if (selection.mode === 'ids' && selection.ids.length === 0) return;
    setFailure(null);
    setTitles(picked);
    try {
      const response = await selectionRequest.run(selection, (body) =>
        add.mutateAsync({ selection: body, pinned }),
      );
      if (response === null) {
        add.reset();
        return;
      }
      if (response.failed > 0) {
        setResult({ response, description: t('pool.resultDescription') });
        return;
      }
      // Re-adding a member answers 200 too: count only the ones that joined.
      const { added, unchanged } = memberAddOutcome(response);
      if (added === 0) toast.success(t('toasts.membersUnchanged', { count: unchanged }));
      else if (unchanged > 0) {
        toast.success(t('toasts.membersAddedSome', { count: added, unchanged }));
      } else toast.success(t('toasts.membersAdded', { count: added }));
    } catch (error) {
      setFailure({ error, action: 'addMembers' });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t('pool.intro')}</p>
      {failure ? (
        <ScheduleErrorAlert error={failure.error} action={failure.action} titles={titles} />
      ) : null}
      <DataTable
        pillFilter="source"
        table={table}
        search={{ placeholder: t('pool.search'), label: t('pool.search') }}
        loading={query.isPending}
        fetching={query.isFetching || add.isPending || bulk.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
        getRowLabel={(row) => row.product.title}
        resourceName={{ singular: t('pool.resource.one'), plural: t('pool.resource.other') }}
        emptyState={{
          title:
            list.q || Object.keys(list.filters).length > 0 ? t('pool.noMatches') : t('pool.empty'),
        }}
        rowActions={rowActions}
        bulkActions={bulkActions}
        toolbarActions={
          canWrite ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setAdding(true)}
              disabled={add.isPending}
              data-tour="schedules.detail.pool-add"
            >
              <Plus aria-hidden />
              {add.isPending ? t('pool.adding') : t('pool.add')}
            </Button>
          ) : null
        }
        tour={{
          table: 'schedules.detail.pool-table',
          search: 'schedules.detail.pool-search',
          filters: 'schedules.detail.pool-filters',
        }}
      />
      {canWrite ? (
        <>
          <AddMembersDialog
            open={adding}
            onOpenChange={setAdding}
            scheduleId={schedule.id}
            onAdd={(picked) => void addProducts(picked)}
          />
          {selectionRequest.dialog}
          <RemoveMemberDialog
            scheduleId={schedule.id}
            member={removing}
            onOpenChange={(open) => {
              if (!open) setRemoving(null);
            }}
            onReleased={offerRun}
            onExcludedInstead={(member) =>
              toast.info(t('toasts.excludedInsteadOne', { title: member.product.title }))
            }
          />
          <ConfirmOfflineDialog request={confirm} onClose={() => setConfirm(null)} />
        </>
      ) : null}
      <BulkResultDialog
        result={result?.response ?? null}
        titles={titles}
        fallbackTitle={(id) => t('pool.unknownProduct', { id })}
        lookupProductTitles
        description={result?.description ?? ''}
        detail={(item) => (
          <MemberResultDetail
            item={item}
            storeNames={storeNames}
            onNavigate={() => setResult(null)}
          />
        )}
        onClose={() => setResult(null)}
      />
    </div>
  );
}
